import { DefaultFrequency } from '@core/constants/data-types.constants';
import {
  generateLifePlanInsurance,
  generateLifePlanInsurances,
  getInsuranceTarget,
  mixLifePlanInsurances
} from '@core/models/insurance.model';
import { deepCloneLifePlan, DefaultLifePlan, LifePlan } from '@core/models/life-plan.model';
import {
  adjustValuesToCurrency,
  applyProductTemporalityConditionLimits,
  updateProductWithLiteValues,
  updateProductWithLiteValuesAndLimits
} from '@core/models/product.model';
import { setRecommendedProducts } from '@core/models/products-per-insurance-target.model';
import { Action, createReducer, on } from '@ngrx/store';
import * as actions from '@redux/actions';

export interface LifePlanState {
  selectedLifePlan: 'current' | 'default';
  currentTargetId: string;
  currentLifePlan: LifePlan;
  defaultLifePlan: LifePlan;
}

export const initialLifePlanState: LifePlanState = {
  selectedLifePlan: 'current',
  currentTargetId: '',
  currentLifePlan: DefaultLifePlan,
  defaultLifePlan: DefaultLifePlan
};

const _lifePlanReducer = createReducer(
  initialLifePlanState,
  on(actions.fullUpdateLifePlan, (state, { newLifePlan }) => {
    let lifePlan = deepCloneLifePlan(newLifePlan);
    lifePlan = {
      frequency: DefaultFrequency,
      ...lifePlan
    };
    return { ...state, currentLifePlan: lifePlan };
  }),
  // AGENT - PERSONAL FORM
  on(
    actions.agentTurnedBackLifePlanPersonalData,
    actions.agentSavedLifePlanPersonalDataDraft,
    actions.agentCompletedLifePlanPersonalData,
    (state, { userPersonalData, spousePersonalData }) => {
      const lifePlan = deepCloneLifePlan(state.currentLifePlan);
      // TODO: maybe this should only be performed if lifePlan.status is DRAFT
      if (!lifePlan.user) {
        lifePlan.user = {};
      }
      lifePlan.user.personal = userPersonalData;
      if (spousePersonalData) {
        if (!lifePlan.spouse) {
          lifePlan.spouse = {};
        }
        lifePlan.spouse.personal = spousePersonalData;
      } else {
        lifePlan.spouse = undefined;
      }
      lifePlan.status = 'DRAFT';
      return { ...state, currentLifePlan: lifePlan };
    }
  ),
  // AGENT - PROFESSIONAL FORM
  on(
    actions.agentTurnedBackLifePlanProfessionalData,
    actions.agentSavedLifePlanProfessionalDataDraft,
    actions.agentCompletedLifePlanProfessionalData,
    (state, { userProfessionalData, spouseProfessionalData }) => {
      const lifePlan = deepCloneLifePlan(state.currentLifePlan);
      // TODO: maybe this should only be performed if lifePlan.status is DRAFT
      if (!lifePlan.user) {
        lifePlan.user = {};
      }
      lifePlan.user.professional = userProfessionalData;
      if (spouseProfessionalData && lifePlan.spouse) {
        lifePlan.spouse.professional = spouseProfessionalData;
      }
      lifePlan.status = 'DRAFT';
      return { ...state, currentLifePlan: lifePlan };
    }
  ),
  // AGENT - FINANCIAL FORM
  on(
    actions.agentTurnedBackLifePlanFinancialData,
    actions.agentSavedLifePlanFinacialDataDraft,
    actions.agentCompletedLifePlanFinancialData,
    (state, { userFinancialData, spouseFinancialData }) => {
      const lifePlan = deepCloneLifePlan(state.currentLifePlan);
      // TODO: maybe this should only be performed if lifePlan.status is DRAFT
      if (!lifePlan.user) {
        lifePlan.user = {};
      }
      lifePlan.user.financial = userFinancialData;
      if (spouseFinancialData && lifePlan.spouse) {
        lifePlan.spouse.financial = spouseFinancialData;
      }
      lifePlan.status = 'DRAFT';
      return { ...state, currentLifePlan: lifePlan };
    }
  ),
  // AGENT - INSURANCES FORM
  on(
    actions.agentTurnedBackLifePlanInsurancesData,
    actions.agentSavedLifePlanInsurancesDataDraft,
    (state, { userInsurancesData, spouseInsurancesData }) => {
      const lifePlan = deepCloneLifePlan(state.currentLifePlan);
      // TODO: maybe this should only be performed if lifePlan.status is DRAFT
      if (!lifePlan.user) {
        lifePlan.user = {};
      }
      lifePlan.user.insurances = userInsurancesData;
      if (spouseInsurancesData && lifePlan.spouse) {
        lifePlan.spouse.insurances = spouseInsurancesData;
      }
      lifePlan.status = 'DRAFT';
      return { ...state, currentLifePlan: lifePlan };
    }
  ),
  on(actions.agentCompletedLifePlanInsurancesData, (state, { userInsurancesData, spouseInsurancesData }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    // TODO: maybe this should only be performed if lifePlan.status is DRAFT
    if (!lifePlan.user) {
      lifePlan.user = {};
    }
    lifePlan.user.insurances = userInsurancesData;
    if (spouseInsurancesData && lifePlan.spouse) {
      lifePlan.spouse.insurances = spouseInsurancesData;
    }
    delete lifePlan.insurances;
    lifePlan.status = 'NORMAL';
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.completedLifePlanInsurancesDataEdit, (state, { userInsurancesData, spouseInsurancesData }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    // TODO: maybe this should only be performed if lifePlan.status is DRAFT
    if (!lifePlan.user) {
      lifePlan.user = {};
    }
    lifePlan.user.insurances = userInsurancesData;
    if (spouseInsurancesData && lifePlan.spouse) {
      lifePlan.spouse.insurances = spouseInsurancesData;
    }
    return { ...state, currentLifePlan: lifePlan };
  }),
  // EDIT FORMS
  on(actions.completedLifePlanPersonalDataEdit, (state, { userPersonalData, spousePersonalData }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    if (!lifePlan.user) {
      lifePlan.user = {};
    }
    lifePlan.user.personal = userPersonalData;
    if (spousePersonalData) {
      if (!lifePlan.spouse) {
        lifePlan.spouse = {};
      }
      lifePlan.spouse.personal = spousePersonalData;
    } else {
      lifePlan.spouse = undefined;
    }
    lifePlan.status = 'NORMAL';
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.completedLifePlanProfessionalDataEdit, (state, { userProfessionalData, spouseProfessionalData }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    if (!lifePlan.user) {
      lifePlan.user = {};
    }
    lifePlan.user.professional = userProfessionalData;
    if (spouseProfessionalData && lifePlan.spouse) {
      lifePlan.spouse.professional = spouseProfessionalData;
    }
    lifePlan.status = 'NORMAL';
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.completedLifePlanFinancialDataEdit, (state, { userFinancialData, spouseFinancialData }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    if (!lifePlan.user) {
      lifePlan.user = {};
    }
    lifePlan.user.financial = userFinancialData;
    if (spouseFinancialData && lifePlan.spouse) {
      lifePlan.spouse.financial = spouseFinancialData;
    }
    lifePlan.status = 'NORMAL';
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.completedFrequencyEdit, (state, { frequency }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    lifePlan.frequency = frequency;
    return { ...state, currentLifePlan: lifePlan };
  }),
  // FUTURE PLANS
  on(actions.completedFuturePlansEdit, (state, { futurePlans }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    if (!lifePlan.user) {
      lifePlan.user = {};
    }
    if (lifePlan.user.financial) {
      lifePlan.user.financial.futurePlans = futurePlans;
    }
    lifePlan.status = 'NORMAL';
    return { ...state, currentLifePlan: lifePlan };
  }),
  // COMPLETE FORM
  on(actions.completedCompleteFormEdit, (state, { user, spouse }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    lifePlan.user = user;
    if (spouse) {
      lifePlan.spouse = spouse;
    }
    return { ...state, currentLifePlan: lifePlan };
  }),
  // INSURANCE TARGETS
  on(actions.generateLifePlanInsuranceTargets, (state, { config }) => {
    const oldTargets = state.currentLifePlan.insurances;
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    let newTargets;
    if (lifePlan.type === 'COMPLETE') {
      newTargets = generateLifePlanInsurances(lifePlan, config);
    } else {
      newTargets = generateLifePlanInsurance(lifePlan, config);
    }
    const mixedTargets = mixLifePlanInsurances(newTargets, oldTargets);
    lifePlan.insurances = mixedTargets;
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.updateLifePlanInsuranceTargetsWithRecommendations, (state, { productsPerInsuranceTarget }) => {
    let lifePlan = deepCloneLifePlan(state.currentLifePlan);
    lifePlan = setRecommendedProducts(lifePlan, productsPerInsuranceTarget);
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.updateLifePlanCurrentInsuranceTarget, (state, { insuranceTargetId }) => {
    return { ...state, currentTargetId: insuranceTargetId };
  }),
  on(actions.updateLifePlanSavingInsuranceTargetTotalAmount, (state, { insuranceTargetId, totalAmount }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
    if (insuranceTarget && insuranceTarget.type === 'saving') {
      insuranceTarget.totalAmount = {
        amount: totalAmount.amount,
        currency: totalAmount.currency
      };
      const relatedFuturePlan = lifePlan.user?.financial?.futurePlans?.find(fp => fp.id === insuranceTarget.id);
      if (relatedFuturePlan) {
        relatedFuturePlan.goalAmount = {
          amount: totalAmount.amount,
          currency: totalAmount.currency
        };
      }
    }
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.updateLifePlanSavingInsuranceTargetSavings, (state, { insuranceTargetId, savings }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
    if (insuranceTarget && insuranceTarget.type === 'saving') {
      insuranceTarget.savings = {
        amount: savings.amount,
        currency: savings.currency
      };
      const relatedFuturePlan = lifePlan.user?.financial?.futurePlans?.find(fp => fp.id === insuranceTarget.id);
      if (relatedFuturePlan) {
        relatedFuturePlan.assuredAmount = {
          amount: savings.amount,
          currency: savings.currency
        };
      }
    }
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.updateLifePlanInsuranceTargetCurrentProduct, (state, { insuranceTargetId, product }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
    if (insuranceTarget) {
      insuranceTarget.currentProduct = product;
    }
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.updateLifePlanInsuranceTargetCurrentProductTemporality, (state, { insuranceTargetId, temporalityId }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
    if (insuranceTarget) {
      const product = insuranceTarget.currentProduct;
      if (product) {
        product.selectedTemporality = temporalityId;
        let temporalitySelected = product.temporalityConditions.find(t => t.id === product.selectedTemporality);
        if (!temporalitySelected) {
          temporalitySelected = product.temporalityConditions[0];
        }
        applyProductTemporalityConditionLimits(product, temporalitySelected);
      }
    }
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(
    actions.updateLifePlanInsuranceTargetCurrentProductWithLiteValues,
    (state, { insuranceTargetId, productId, liteResponse }) => {
      const lifePlan = deepCloneLifePlan(state.currentLifePlan);
      const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
      if (insuranceTarget?.currentProduct?.id === productId) {
        const product = insuranceTarget.currentProduct;
        updateProductWithLiteValues(product, liteResponse);
      }
      return { ...state, currentLifePlan: lifePlan };
    }
  ),
  on(
    actions.updateLifePlanInsuranceTargetCurrentProductCurrencies,
    (state, { insuranceTargetId, productId, currencyExchange }) => {
      const lifePlan = deepCloneLifePlan(state.currentLifePlan);
      const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
      if (insuranceTarget?.currentProduct?.id === productId) {
        const product = insuranceTarget.currentProduct;
        adjustValuesToCurrency(product, product.selectedCurrency, currencyExchange);
      }
      return { ...state, currentLifePlan: lifePlan };
    }
  ),
  on(
    actions.updateLifePlanInsuranceTargetCurrentProductWithLiteValuesAndLimits,
    (state, { insuranceTargetId, productId, liteResponses }) => {
      const lifePlan = deepCloneLifePlan(state.currentLifePlan);
      const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
      if (insuranceTarget?.currentProduct?.id === productId) {
        const product = insuranceTarget.currentProduct;
        updateProductWithLiteValuesAndLimits(product, liteResponses);
      }
      return { ...state, currentLifePlan: lifePlan };
    }
  ),
  on(actions.updateLifePlanCurrentProductOnCart, (state, { insuranceTargetId, productId, onCart }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
    if (insuranceTarget?.currentProduct?.id === productId) {
      insuranceTarget.currentProduct.cartStatus.added = onCart;
    }
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.updateLifePlanSeveralProductsOnCart, (state, { insuranceTargets, onCart }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    for(let i = 0; i < insuranceTargets.length; i++){
      const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargets[i]);
      insuranceTarget!.currentProduct!.cartStatus.added = onCart;
    }
    return { ...state, currentLifePlan: lifePlan };
  }),
  on(actions.updateDefaultLifePlanCurrentProductOnCart, (state, { insuranceTargetId, productId, onCart }) => {
    const lifePlan = deepCloneLifePlan(state.defaultLifePlan);
    const insuranceTarget = getInsuranceTarget(lifePlan, insuranceTargetId);
    if (insuranceTarget?.currentProduct?.id === productId) {
      insuranceTarget.currentProduct.cartStatus.added = onCart;
    }
    return { ...state, defaultLifePlan: lifePlan };
  }),
  on(actions.updateInsuranceTargetUniversity, (state, { insuranceTargetId, university, universityCost }) => {
    const lifePlan = deepCloneLifePlan(state.currentLifePlan);
    const child = lifePlan.user?.personal?.children?.find(c => c.id === insuranceTargetId);
    if (child) {
      child.university = university;
      child.universityCost = universityCost;
    }
    if (lifePlan.insurances) {
      lifePlan.insurances[insuranceTargetId].insured.university = university;
      lifePlan.insurances[insuranceTargetId].insured.universityCost = universityCost;
    }
    return { ...state, currentLifePlan: lifePlan };
  }),
  // Life Plan management
  on(actions.forceUpdateDefaultLifePlan, (state, { defaultLifePlan }) => {
    return { ...state, defaultLifePlan: deepCloneLifePlan(defaultLifePlan) };
  }),
  on(actions.updateSelectedLifePlan, (state, { selectedLifePlan }) => {
    const newState = JSON.parse(JSON.stringify(state));
    newState.selectedLifePlan = selectedLifePlan;
    return { ...newState };
  }),
  on(actions.cleanLifePlanState, state => {
    return { ...initialLifePlanState };
  })
);

export function lifePlanReducer(state: LifePlanState | undefined, action: Action) {
  return _lifePlanReducer(state, action);
}
